import { useFormContext } from "react-hook-form";
import AutoResizeTextarea from "./input/AutoResizeTextarea";
import QuillEditor from "./input/QuillEditor";
import InputList from "./input/InputList";
import InputCompactList from "./input/InputCompactList";
import { useTranslation } from "react-i18next";
import ImagePicker from "../image/ImagePicker";
import MediaPicker from "../media/MediaPicker";
import Switch from "./input/switch";
import { Theme } from "../../../theme";
import { FormConfig, FormFieldType } from "./formTypes";
import SearchableMultiSelect from "./input/SearchableMultiSelect";
import { useAdminPermissions } from "../../../core/hooks/useAdminPermissions";

interface FormFieldProps {
  formConfig: FormConfig;
  readOnly: boolean;
}

export function FormField({ formConfig, readOnly }: FormFieldProps) {
  const { name, label, type, placeholder, settings } = formConfig;

  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext();
  const { role } = useAdminPermissions();

  if (settings?.hideForViewer && role === "viewer") {
    return null;
  }

  // Vérifier si le champ doit être affiché
  if (settings?.show) {
    const watchedValue = watch(settings.show.when);
    if (
      (typeof settings.show.is === "string" &&
        watchedValue !== settings.show.is) ||
      (settings.show.is instanceof Array &&
        !settings.show.is.includes(watchedValue))
    ) {
      return null;
    }
  }

  const error = errors[name];

  return (
    <div className="space-y-1">
      <label htmlFor={name} className={Theme.label}>
        {t(label)}
      </label>
      {type === FormFieldType.RichText ? (
        <QuillEditor
          value={getValues(name)}
          onChange={(value: string) => {
            setValue(name, value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          readOnly={readOnly}
        />
      ) : type === FormFieldType.Textarea ? (
        <AutoResizeTextarea
          registration={register(name)}
          value={watch(name)}
          readOnly={readOnly}
          placeholder={placeholder ? t(placeholder) : ""}
          className={`select-none ${Theme.textarea} min-h-[2.75rem] resize-none`}
        />
      ) : type === FormFieldType.List ? (
        <InputList
          value={getValues(name) ?? []}
          onChange={(value: string[]) => setValue(name, value)}
          readOnly={readOnly}
        />
      ) : type === FormFieldType.CompactList ? (
        <InputCompactList
          id={name}
          value={watch(name) ?? []}
          onChange={(v: string[]) =>
            setValue(name, v, { shouldValidate: true, shouldDirty: true })
          }
          readOnly={readOnly}
          rows={settings?.rows}
          placeholder={placeholder ? t(placeholder) : ""}
        />
      ) : type === FormFieldType.Image ? (
        <ImagePicker
          value={watch(name) ?? null}
          onSelect={(value: string | File | null) => {
            if (value !== null) setValue(name, value);
          }}
          format={settings?.format ?? 1}
          readOnly={readOnly}
        />
      ) : type === FormFieldType.Media ? (
        <MediaPicker
          mediaType={settings?.mediaType ?? "video"}
          value={watch(name) ?? null}
          onSelect={(value: File | null) =>
            setValue(name, value, { shouldValidate: true, shouldDirty: true })
          }
          readOnly={readOnly}
        />
      ) : type === FormFieldType.Select ? (
        <select
          {...register(name)}
          disabled={readOnly}
          className={`custom-select ${Theme.input} select-none`}
        >
          {settings?.nullOption && <option value="">{t("form.none")}</option>}
          {Object.keys(settings?.options || {}).map((key: string) => (
            <option key={key} value={key}>
              {t(settings?.options?.[key] ?? "")}
            </option>
          ))}
        </select>
      ) : type === FormFieldType.MultiSelect ? (
        <SearchableMultiSelect
          id={name}
          value={watch(name) ?? []}
          onChange={(next) =>
            setValue(name, next, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          options={settings?.options ?? {}}
          readOnly={readOnly}
          hasError={!!error}
          hint={placeholder ? t(placeholder) : undefined}
        />
      ) : type === FormFieldType.Switch ? (
        <Switch
          id={name}
          checked={watch(name)}
          onChange={(value: boolean) =>
            setValue(name, value, { shouldDirty: true, shouldValidate: true })
          }
          readOnly={readOnly}
        />
      ) : (
        <input
          {...register(name)}
          type={String(type)}
          id={name}
          placeholder={placeholder ? t(placeholder) : ""}
          readOnly={readOnly}
          className={`block w-full ${Theme.input}
            ${
              error
                ? "border-red-500 text-red-900 placeholder-red-500 dark:border-red-400 dark:text-red-200 dark:placeholder-red-300"
                : ""
            }`}
        />
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {error.message as string}
        </p>
      )}
    </div>
  );
}
